import { Button, CircularProgress, Grid } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { ConditionalTooltip } from 'src/components/ConditionalTooltip'
import { useAcoesTabelaFrete } from '../hooks/useAcoesTabelaFrete'
import { useCenariosResolvidos } from '../hooks/useCenariosResolvidos'
import { LotesDetail } from '../types'
import { EstadoAcao, resolverEstadoAcoes } from '../utils/acoesTabelaFrete'
import { CenariosResolvidosPanel } from './CenariosResolvidosPanel'
import { HistoricoIntegracaoTabelaFrete } from './HistoricoIntegracaoTabelaFrete'
import { StatusIntegracaoAcoes } from './StatusIntegracaoAcoes'

interface BotaoAcaoProps {
  estado: EstadoAcao
  label: string
  loading: boolean
  // Desabilita enquanto qualquer ação estiver rodando, para não disparar duas integrações ao mesmo tempo
  bloqueado: boolean
  onClick: () => void
  destaque?: boolean
}

function BotaoAcao({
  estado,
  label,
  loading,
  bloqueado,
  onClick,
  destaque
}: BotaoAcaoProps) {
  if (!estado.visivel) return null

  return (
    <Grid item>
      <ConditionalTooltip
        enabled={!estado.habilitado && !!estado.motivo}
        title={estado.motivo ?? ''}
      >
        <Button
          type="button"
          style={{ minWidth: 210, whiteSpace: 'nowrap' }}
          variant={destaque ? 'contained' : 'outlined'}
          color={destaque ? 'secondary' : 'primary'}
          disabled={!estado.habilitado || bloqueado}
          onClick={onClick}
        >
          {loading ? <CircularProgress color="inherit" size={24} /> : label}
        </Button>
      </ConditionalTooltip>
    </Grid>
  )
}

interface Props {
  lote: LotesDetail
  // Tela de Validar Lotes, com a permissão da tela e lote em análise
  emValidacao: boolean
  onChanged: () => void
}

export function AcoesTabelaFrete({ lote, emValidacao, onChanged }: Props) {
  const estado = resolverEstadoAcoes(lote, emValidacao)
  const acoes = useAcoesTabelaFrete(lote.id, onChanged)
  const cenarios = useCenariosResolvidos(lote.id)
  const [painelAberto, setPainelAberto] = useState(false)
  const [historicoAberto, setHistoricoAberto] = useState(false)

  // Busca os cenários já aplicados assim que o lote é aberto (sem esperar um clique em
  // "Reprocessar cenários"), mesmo com o painel ainda fechado — ao expandir, o resultado já
  // está pronto. "Reprocessar" continua existindo pra forçar uma nova leitura (o resultado pode
  // mudar se o cadastro de cenários ou o próprio lote mudarem nesse meio-tempo).
  useEffect(() => {
    if (estado.criarTabelaFrete.visivel) {
      cenarios.reprocessar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lote.id, estado.criarTabelaFrete.visivel])

  if (!estado.criarTabelaFrete.visivel) return null

  const executando = acoes.executando !== null

  const reprocessar = () => {
    setPainelAberto(true)
    cenarios.reprocessar()
  }

  return (
    <>
      <CenariosResolvidosPanel
        aberto={painelAberto}
        loading={cenarios.loading}
        resolucao={cenarios.resolucao}
        erro={cenarios.erro}
        onToggle={setPainelAberto}
        onReprocessar={reprocessar}
      />

      <StatusIntegracaoAcoes lote={lote} />

      <Grid
        container
        justifyContent="flex-end"
        spacing={2}
        style={{ marginTop: 8 }}
      >
        <BotaoAcao
          estado={estado.criarTabelaFrete}
          label="Criar Tabela de Frete"
          loading={acoes.executando === 'criarTabelaFrete'}
          bloqueado={executando}
          onClick={acoes.criarTabelaFrete}
        />
        <BotaoAcao
          estado={estado.criarPC}
          label="Criar PC"
          loading={acoes.executando === 'criarPC'}
          bloqueado={executando}
          onClick={acoes.criarPC}
        />
        <BotaoAcao
          estado={estado.criarTabelaCliente}
          label="Criar Tabela por Cliente"
          loading={acoes.executando === 'criarTabelaCliente'}
          bloqueado={executando}
          onClick={acoes.criarTabelaCliente}
        />
        <BotaoAcao
          estado={estado.reajuste}
          label="Reajuste de Valores"
          loading={acoes.executando === 'reajuste'}
          bloqueado={executando}
          onClick={acoes.reajustar}
          destaque={!!lote.reajustePendente}
        />
        <Grid item>
          <Button
            type="button"
            style={{ minWidth: 210, whiteSpace: 'nowrap' }}
            variant="outlined"
            onClick={() => setHistoricoAberto(true)}
          >
            Histórico de integração
          </Button>
        </Grid>
      </Grid>

      <HistoricoIntegracaoTabelaFrete
        open={historicoAberto}
        loteId={lote.id}
        onClose={() => setHistoricoAberto(false)}
      />
    </>
  )
}
